import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { contentApi } from '../../services/contentApi';
import { ProjectListItem } from '../../types/content';
import Button from '../../components/UI/Button';
import CourseCoverImage from '../../components/content/CourseCoverImage';
import { COURSE_IMAGES } from '../../config/courseImages';
import { canWriteProjects, isOwner } from '../../utils/roles';
import { useAuth } from '../../context/AuthContext';
import { confirmAction } from '../../utils/confirmAction';

const MyProjectsPage: React.FC = () => {
  const { user } = useAuth();
  const owner = isOwner(user);
  const [projects, setProjects] = useState<ProjectListItem[]>([]);

  useEffect(() => {
    contentApi.myProjects().then((r) => setProjects(r.data.projects || [])).catch(() => undefined);
  }, []);

  if (!canWriteProjects(user)) {
    return <div className="max-w-4xl mx-auto px-4 py-10 text-gray-600">You do not have permission to create projects.</div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">My Projects</h1>
        <Link to="/admin/projects/new"><Button>New project</Button></Link>
      </div>
      <div className="mt-6 space-y-3">
        {projects.length === 0 && <p className="text-gray-500">You have not created any projects yet.</p>}
        {projects.map((gig) => (
          <div key={gig.id} className="rounded-xl border border-gray-200 p-4 hover:border-primary-200 bg-white flex justify-between gap-3">
            <Link to={`/admin/projects/${gig.id}/edit`} className="flex-1 min-w-0 flex gap-3">
              <CourseCoverImage
                src={gig.coverImageUrl}
                fallbackSrc={COURSE_IMAGES.projectsHero}
                className="h-16 w-24 rounded-lg object-cover shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="flex justify-between gap-2">
                  <h2 className="font-semibold text-gray-900">{gig.title}</h2>
                  <span className="text-sm text-gray-500 shrink-0">{gig.status}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{gig.company} · {gig.summary || gig.subtitle}</p>
              </div>
            </Link>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <button
                type="button"
                className="text-sm text-error-600"
                onClick={async () => {
                  const ok = await confirmAction({
                    title: 'Archive this project?',
                    description: `“${gig.title}” will be deactivated (soft delete).`,
                    confirmLabel: 'Archive',
                    cancelLabel: 'Keep it',
                    variant: 'danger',
                  });
                  if (!ok) return;
                  contentApi.archiveProject(gig.id).then(() => setProjects((current) => current.filter((item) => item.id !== gig.id)));
                }}
              >
                Archive
              </button>
              {owner ? (
                <button
                  type="button"
                  className="text-sm text-error-700 font-medium"
                  onClick={async () => {
                    const ok = await confirmAction({
                      title: 'Delete project permanently?',
                      description: `“${gig.title}” will be permanently removed. This cannot be undone.`,
                      confirmLabel: 'Delete permanently',
                      cancelLabel: 'Cancel',
                      variant: 'danger',
                    });
                    if (!ok) return;
                    contentApi.permanentDeleteProject(gig.id).then(() => setProjects((current) => current.filter((item) => item.id !== gig.id)));
                  }}
                >
                  Delete permanently
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyProjectsPage;
